// src/auth/AuthContext.js
import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { CacheLookupPolicy, PublicClientApplication } from '@azure/msal-browser';
import { MsalProvider, useIsAuthenticated, useMsal } from '@azure/msal-react';
import { initializeMsalConfig, loginRequest, graphFileRequest } from './authConfig';
import {
  getApiBaseUrl,
  setTokenRefreshCallback,
  setInteractiveReauthCallback,
  setAuthNoticeCallback,
  isTokenExpiringSoon,
  decodeJwtPayload,
} from '../utils/api';
import { dlog } from '../utils/debug';

// Create Auth Context
const AuthContext = createContext(null);

// MSAL instance - will be initialized later
let msalInstance = null;

// Initialize MSAL
export const initializeMSAL = async () => {
  try {
    const msalConfig = await initializeMsalConfig();
    msalInstance = new PublicClientApplication(msalConfig);
    await msalInstance.initialize();
    return msalInstance;
  } catch (error) {
    console.error('Failed to initialize MSAL:', error);
    throw error;
  }
};

// Auth Provider Component
export const AuthProvider = ({ children, msalInstance }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [accessToken, setAccessToken] = useState(localStorage.getItem('access_token'));
  const [authError, setAuthError] = useState(null);
  // Brief, user-facing "reconnecting" notice -- set right before an
  // interactive re-auth redirect fires, so the resulting navigation isn't
  // a total surprise. See utils/api.js's setAuthNoticeCallback.
  const [authNotice, setAuthNotice] = useState(null);

  return (
    <MsalProvider instance={msalInstance}>
      <AuthContext.Provider value={{
        user,
        setUser,
        isLoading,
        setIsLoading,
        accessToken,
        setAccessToken,
        authError,
        setAuthError,
        authNotice,
        setAuthNotice
      }}>
        <AuthManager>
          {children}
        </AuthManager>
      </AuthContext.Provider>
    </MsalProvider>
  );
};

// Auth Manager Component - handles authentication logic
const AuthManager = ({ children }) => {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const {
    user,
    setUser,
    setIsLoading,
    setAccessToken,
    setAuthError,
    setAuthNotice,
  } = useContext(AuthContext);

  const refreshTimerRef = useRef(null);

  // ------------------------------------------------------------------
  // Core helper: acquire a fresh Azure token silently and exchange it
  // for a new backend JWT.  Returns the new JWT string or null.
  // ------------------------------------------------------------------
  const silentRefresh = useCallback(async () => {
    if (!isAuthenticated || accounts.length === 0) return null;

    try {
      const account = accounts[0];
      const tokenRequest = { ...loginRequest, account };
      const msalResponse = await instance.acquireTokenSilent(tokenRequest);
      const azureToken = msalResponse.accessToken;

      // Get user info from Microsoft Graph
      const graphResponse = await fetch('https://graph.microsoft.com/v1.0/me', {
        headers: { 'Authorization': `Bearer ${azureToken}` }
      });
      if (!graphResponse.ok) throw new Error('Graph call failed during refresh');
      const userInfo = await graphResponse.json();

      // Exchange for backend JWT. `credentials: 'include'` is required so
      // the kai_session cookie is set/sent on cross-origin dev requests
      // (localhost:3000 -> localhost:8082); in production they share an
      // origin so this is a no-op.
      const backendResponse = await fetch(`${getApiBaseUrl()}/auth/exchange`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${azureToken}`
        },
        body: JSON.stringify({ azure_token: azureToken, user_info: userInfo })
      });

      if (backendResponse.ok) {
        const authData = await backendResponse.json();
        localStorage.setItem('access_token', authData.access_token);
        setAccessToken(authData.access_token);
        setUser(authData.user);
        dlog('Silent token refresh successful');
        return authData.access_token;
      }
    } catch (err) {
      console.warn('Silent token refresh failed:', err);
    }
    return null;
  }, [isAuthenticated, accounts, instance, setAccessToken, setUser]);

  // ------------------------------------------------------------------
  // Register the refresh callback so authenticatedFetch can call it
  // ------------------------------------------------------------------
  useEffect(() => {
    setTokenRefreshCallback(silentRefresh);
    return () => setTokenRefreshCallback(null);
  }, [silentRefresh]);

  // ------------------------------------------------------------------
  // Interactive fallback for when acquireTokenSilent can't satisfy a
  // refresh -- same account/scopes as silentRefresh above, but a full
  // top-level redirect (acquireTokenRedirect) instead of a hidden iframe,
  // which is what makes it immune to the third-party-cookie/tracking-
  // prevention restrictions that broke the silent path. Same MSAL method
  // login() above already uses (loginRedirect), so the return trip needs
  // no new plumbing -- index.js already calls handleRedirectPromise() on
  // every load. Called by authenticatedFetch only as a last resort.
  //
  // Deliberately NOT an async function: api.js's caller needs the "no
  // account to re-auth with" precondition to throw SYNCHRONOUSLY, so it
  // can tell "this never even started" (fall back to the old /login
  // redirect) apart from "it started, whatever happens next is out of our
  // hands" (an async function turns every throw into a rejected promise,
  // collapsing that distinction) -- see attemptInteractiveReauth's own
  // comment in api.js. acquireTokenRedirect's own promise is returned
  // as-is for the caller to attach a fire-and-forget .catch() to, not
  // awaited here.
  // ------------------------------------------------------------------
  const interactiveReauth = useCallback(() => {
    if (accounts.length === 0) {
      throw new Error('No MSAL account available to re-authenticate');
    }
    return instance.acquireTokenRedirect({ ...loginRequest, account: accounts[0] });
  }, [accounts, instance]);

  useEffect(() => {
    setInteractiveReauthCallback(interactiveReauth);
    setAuthNoticeCallback(setAuthNotice);
    return () => {
      setInteractiveReauthCallback(null);
      setAuthNoticeCallback(null);
    };
  }, [interactiveReauth, setAuthNotice]);

  // ------------------------------------------------------------------
  // Proactive refresh timer – check every 60 s whether the token is
  // about to expire (within 5 min) and refresh it in the background.
  // ------------------------------------------------------------------
  useEffect(() => {
    const scheduleRefreshCheck = () => {
      refreshTimerRef.current = setInterval(async () => {
        if (isTokenExpiringSoon(300)) {  // 5-minute buffer
          dlog('Token expiring soon – refreshing proactively');
          await silentRefresh();
        }
      }, 60_000); // check every 60 seconds
    };

    if (isAuthenticated && accounts.length > 0) {
      scheduleRefreshCheck();
    }

    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [isAuthenticated, accounts.length, silentRefresh]);

  // ------------------------------------------------------------------
  // Initial auth flow (unchanged logic, extracted exchangeTokenWithBackend)
  // ------------------------------------------------------------------
  useEffect(() => {
    const initializeAuth = async () => {
      dlog('Initializing auth...', { isAuthenticated, hasAccounts: accounts.length > 0, hasStoredToken: !!localStorage.getItem('access_token'), hasUser: !!user });
      
      setIsLoading(true);
      
      try {
        // Check if we have a stored token first
        const storedToken = localStorage.getItem('access_token');
        
        if (storedToken && !user) {
          dlog('Verifying stored token...');

          // Quick client-side expiry check before hitting the backend
          if (isTokenExpiringSoon(0)) {
            dlog('Stored token expired, attempting silent refresh...');
            const refreshed = await silentRefresh();
            if (refreshed) {
              setIsLoading(false);
              return;
            }
            // Refresh failed – fall through to MSAL check below
            localStorage.removeItem('access_token');
            setAccessToken(null);
            setUser(null);
          } else {
            // Verify token with backend
            try {
              const response = await fetch(`${getApiBaseUrl()}/auth/user`, {
                credentials: 'include',
                headers: {
                  'Authorization': `Bearer ${storedToken}`
                }
              });
              
              if (response.ok) {
                const userData = await response.json();
                setUser(userData.user);
                setAccessToken(storedToken);
                setIsLoading(false);
                return;
              } else {
                dlog('Stored token invalid, attempting silent refresh...');
                const refreshed = await silentRefresh();
                if (refreshed) {
                  setIsLoading(false);
                  return;
                }
                localStorage.removeItem('access_token');
                setAccessToken(null);
                setUser(null);
              }
            } catch (error) {
              console.error('Error verifying stored token:', error);
              localStorage.removeItem('access_token');
              setAccessToken(null);
              setUser(null);
            }
          }
        }

        // If we already have a user, don't re-authenticate
        if (user) {
          dlog('User already exists, skipping auth');
          setIsLoading(false);
          return;
        }

        // Only proceed with MSAL if we're authenticated but don't have a backend user yet
        if (isAuthenticated && accounts.length > 0 && !user) {
          dlog('MSAL authenticated, exchanging token...');
          const refreshed = await silentRefresh();
          if (refreshed) {
            setIsLoading(false);
          } else {
            setIsLoading(false);
            setAuthError('Failed to acquire access token');
          }
        } else if (!isAuthenticated && !user) {
          // Not authenticated and no user
          dlog('Not authenticated, showing login');
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Authentication initialization error:', error);
        setAuthError(error.message);
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, [isAuthenticated, accounts.length]); // eslint-disable-line react-hooks/exhaustive-deps

  return children;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

/**
 * A delegated Microsoft Graph token for the signed-in user, scoped to reading
 * files THEY can access. Used for one thing: letting the backend ask Graph
 * whether this person can open a SharePoint file they pasted a link to,
 * instead of asking as KAI's shared app-only credential, which can open
 * anything in the tenant.
 *
 * Silent by default, and null on any failure. A null caller treats as "check
 * skipped", never as "denied" -- a consent prompt fired off the back of
 * someone sending a chat message would be worse than not checking, and a
 * network blip is not a permissions answer.
 *
 * `interactive` opts into a one-off consent prompt when the silent path fails.
 * Only the verification page uses it: the person is sitting on a page whose
 * entire purpose is this check, so a prompt is expected rather than an
 * interruption, and without it the very first use can never succeed. Files
 * .Read.All is a user-consentable delegated permission, so this works on a
 * tenant where nobody has clicked "Grant admin consent" -- each person
 * consents once for themselves and MSAL caches it from then on.
 *
 * `onRedirecting`, if supplied, additionally permits the last-resort route:
 * navigating the whole tab to Microsoft when a popup cannot be opened at all.
 * It is a callback rather than a flag because that navigation is visible and
 * unloads the page, so only a caller that can say so first is allowed to
 * trigger it.
 */
const POPUP_UNAVAILABLE_CODES = new Set(['popup_window_error', 'empty_window_error']);

// The Microsoft account belonging to the person KAI has signed in -- not
// simply the first one cached.
//
// getAllAccounts() returns every identity this browser has ever authenticated,
// in no meaningful order. Taking [0] meant a viewer who also has a personal
// Microsoft account could be checked as THAT account: a guaranteed "you don't
// have access" to a workbook it has never heard of, and an email-the-owner
// button that could not possibly help. Sharing a dashboard is exactly when
// that happens, because the builder's own browser usually has one account and
// nobody notices.
//
// Returns the hint separately so the interactive path can still ask when no
// cached account matches -- Microsoft prompts for the right identity rather
// than this giving up.
const kaiMsalIdentity = () => {
  const accounts = msalInstance.getAllAccounts() || [];
  const claims = decodeJwtPayload(localStorage.getItem('access_token') || '') || {};
  const email = (claims.email || claims.preferred_username || '').toLowerCase();
  if (!email) {
    // No KAI identity to match against (a token that predates these claims).
    // One cached account is unambiguous; more than one is a guess, and
    // guessing wrong is what this function exists to stop.
    return { account: accounts.length === 1 ? accounts[0] : null, loginHint: '' };
  }
  const account = accounts.find((a) =>
    [a.username, a.idTokenClaims?.preferred_username, a.idTokenClaims?.email].some(
      (value) => (value || '').toLowerCase() === email
    )
  );
  return { account: account || null, loginHint: email };
};

export const getGraphFileToken = async ({ interactive = false, onRedirecting } = {}) => {
  if (!msalInstance) return null;
  const { account, loginHint } = kaiMsalIdentity();
  if (!account && !(interactive && loginHint)) return null;

  // Silent and interactive are alternatives, not a sequence. Retrying silent
  // first on the interactive path is what broke consent entirely: the silent
  // flow is a hidden iframe against login.microsoftonline.com, Edge's
  // third-party cookie rules stop it completing, and it takes the full
  // monitor_window_timeout (~10s) to admit that. By then the click that asked
  // for the prompt was no longer recent enough to count as a user gesture, so
  // window.open returned null and the popup failed with popup_window_error --
  // leaving no working route to consent at all. The caller has already tried
  // silent before it offers the button, so nothing is lost by skipping it.
  if (!interactive) {
    try {
      const result = await msalInstance.acquireTokenSilent({
        ...graphFileRequest,
        account,
        // Cached token, or the cached refresh token redeemed over the network
        // -- but never the hidden iframe MSAL would otherwise fall back to.
        // That iframe cannot complete in a browser that blocks third-party
        // cookies, and it spends the whole monitor_window_timeout finding
        // out. Six seconds of spinner to reach a prompt we were always going
        // to have to show is worse than showing it straight away, and the
        // refresh token covers the case that matters: someone who consented
        // once already.
        cacheLookupPolicy: CacheLookupPolicy.AccessTokenAndRefreshToken
      });
      return result?.accessToken || null;
    } catch (error) {
      // consent_required / interaction_required until this person has
      // consented once; monitor_window_timeout when the iframe is blocked.
      // Both mean the same thing here: ask them.
      dlog('Graph file token: silent acquisition failed:', error?.errorCode || error);
      return null;
    }
  }

  try {
    // Nothing may be awaited before this call. acquireTokenPopup opens the
    // window synchronously (asyncPopups defaults to false), which is the only
    // reason the browser lets it through at all.
    const result = await msalInstance.acquireTokenPopup({
      ...graphFileRequest,
      ...(account ? { account } : { loginHint })
    });
    return result?.accessToken || null;
  } catch (error) {
    dlog('Graph file token: interactive acquisition failed:', error?.errorCode || error);

    // A popup the browser refused to open is a browser setting, not an
    // answer about this person's access -- so fall back to a full-tab
    // redirect rather than giving up. MSAL records the current URL
    // (redirectStartPage) and navigateToLoginRequestUrl is on by default, so
    // it navigates back here afterwards with the query string intact and the
    // token in its cache, where the ordinary silent path picks it up.
    //
    // Deliberately not done for user_cancelled: closing the window is a
    // decision, and answering it by seizing the whole tab would be hostile.
    if (onRedirecting && POPUP_UNAVAILABLE_CODES.has(error?.errorCode)) {
      try {
        onRedirecting();
        await msalInstance.acquireTokenRedirect({
          ...graphFileRequest,
          ...(account ? { account } : { loginHint }),
          redirectStartPage: window.location.href
        });
      } catch (redirectError) {
        dlog('Graph file token: redirect acquisition failed:', redirectError?.errorCode || redirectError);
      }
    }
    return null;
  }
};

// Login function
export const login = async () => {
  try {
    if (!msalInstance) {
      throw new Error('MSAL not initialized');
    }
    
    await msalInstance.loginRedirect(loginRequest);
  } catch (error) {
    console.error('Login error:', error);
    throw error;
  }
};

// Logout function
export const logout = async () => {
  try {
    if (!msalInstance) {
      throw new Error('MSAL not initialized');
    }

    // Tell the backend to clear the kai_session cookie. Best-effort —
    // if this call fails we still proceed with local cleanup + MSAL logout.
    const storedToken = localStorage.getItem('access_token');
    if (storedToken) {
      try {
        await fetch(`${getApiBaseUrl()}/auth/logout`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Authorization': `Bearer ${storedToken}` },
        });
      } catch (err) {
        console.warn('Backend logout call failed (continuing):', err);
      }
    }

    // Clear local storage
    localStorage.removeItem('access_token');

    // Clear the last-visited-path fallback (see App.js/AuthCallback.js) so
    // the next sign-in in this tab lands on the post-logout landing page
    // instead of being redirected back into the just-logged-out user's
    // last conversation/project.
    sessionStorage.removeItem('kai_last_app_path');

    // Same idea for App.js's PENDING_PROJECT_SETUP_STORAGE_KEY -- otherwise
    // signing out while a new project's files are still uploading, then
    // signing back in in the same tab, opens that project via App.js's own
    // restore effect for the new session.
    sessionStorage.removeItem('kai_pending_project_setup_id');

    // Logout from MSAL
    await msalInstance.logoutRedirect({
      postLogoutRedirectUri: window.location.origin
    });
  } catch (error) {
    console.error('Logout error:', error);
    throw error;
  }
};

export default AuthContext;
